import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';

import { Add, Delete } from '@mui/icons-material';
import CommonButton from '../../../buttons/common-button';

const AppointmentPartsOrder = ({ 
    appointmentActions, 
    appointmentPartOrders,
    setAppointmentPartOrders
}) => {
    // State to manage the rows of the parts order
    const [rows, setRows] = useState(
        appointmentPartOrders && appointmentPartOrders.length > 0 ? appointmentPartOrders : [{ id: null, name: "", reference: "", quantity: 1 }]
    );

    // Update the rows when the appointmentPartOrders prop changes
    useEffect(() => {
        if (appointmentPartOrders && appointmentPartOrders.length > 0) {
            setRows(appointmentPartOrders);
        }
    }, [appointmentPartOrders]);

    // Update the appointmentPartOrders state when rows change
    useEffect(() => {
        const validRows = rows.filter(r => r.name.trim() !== '' && r.quantity >= 1);

        // If no valid rows, clear orders only if not already empty
        if (validRows.length === 0) {
            if (appointmentPartOrders.length !== 0) {
                setAppointmentPartOrders([]);
            }
            return;
        }

        // Deep compare before updating to avoid infinite loops
        const newRowsJSON = JSON.stringify(validRows);
        const existingJSON = JSON.stringify(appointmentPartOrders);
        if (newRowsJSON !== existingJSON) {
            setAppointmentPartOrders(validRows);
        }
    }, [rows, appointmentPartOrders, setAppointmentPartOrders]);

    /**
     * Handles the change in input fields for each row.
     * @param {number} index - The index of the row being updated
     * @param {string} field - The field being updated (name, reference, quantity)
     * @param {string|number} value - The new value for the field
     */
    const handleChange = (index, field, value) => {
        setRows(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [field]: field === 'quantity' ? parseInt(value, 10) : value
            };
            return updated;
        });
    };

    /**
     * Adds a new row to the parts order.
     * @returns {void}
     */
    const addRow = () => {
        setRows(prev => [
            ...prev,
            { name: "", reference: "", quantity: 1 }
        ]);
    };

    /**
     * Removes a row from the parts order.
     * @param {number} index - The index of the row to remove
     * @returns {void}
     */
    const removeRow = (index) => {
        setRows(prev => prev.filter((_, i) => i !== index));
    };

    // If no actions are provided or the parts order action is not active, return null
    if (!appointmentActions || !appointmentActions.some(action => action.name === "Commande pièce" && action.isActive)) {
        return null; // If no parts order action is active, return null
    }

    // Render the appointment fields
    return (
        <div className="folder-section appointment-parts-order w-100 flex">
            <h2>Commande de pièces</h2>
            <table className="appointment-parts-order-table w-100">
                <thead>
                    <tr>
                        <th>Nom</th>
                        <th>Référence</th>
                        <th>Quantité</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, idx) => (
                        <tr key={idx}>
                            <td data-label="Nom de la pièce">
                                <input
                                    type="text"
                                    className="w-full"
                                    value={row.name}
                                    onChange={e =>
                                        handleChange(idx, "name", e.target.value)
                                    }
                                    placeholder="Nom de la pièce"
                                />
                            </td>
                            <td data-label="Référence de la pièce">
                                <input
                                    type="text"
                                    className="w-full"
                                    value={row.reference}
                                    onChange={e =>
                                        handleChange(idx, "reference", e.target.value)
                                    }
                                    placeholder="Réf. pièce (facultatif)"
                                />
                            </td>
                            <td data-label="Quantité">
                                <input
                                    type="number"
                                    className="w-full"
                                    min="1"
                                    value={row.quantity}
                                    onChange={e =>
                                        handleChange(idx, "quantity", e.target.value)
                                    }
                                />
                            </td>
                            <td>
                                {idx > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => removeRow(idx)}
                                        title="Supprimer cette ligne"
                                        className='btn-remove-row'
                                    >
                                        <Delete />
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <CommonButton
                label="Ajouter une ligne"
                icon={<Add />}
                className="btn-add-row"
                onClick={addRow}
            >
                Ajouter une ligne
            </CommonButton>
        </div>
    );
};

// Prop types for the AppointmentPartsOrder component
AppointmentPartsOrder.propTypes = {
    appointmentActions: PropTypes.array,
    appointmentPartOrders: PropTypes.array,
    setAppointmentPartOrders: PropTypes.func,
};

export default AppointmentPartsOrder;